import { Trans, useTranslation } from "react-i18next";
import { Ref, forwardRef, useImperativeHandle, useState } from "react";
import {
  TransferRead,
  TransferCorrect,
  TransferService,
} from "@/domains/transfer";
import { ErrorHelper } from "@/helpers";
import { useMessage } from "@/contexts";
// components
import { Button, Form, Input, Modal } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";

interface CorrectModalProps {
  onCheck: () => void;
}

export interface CorrectModalRef {
  open: (transfer: TransferRead) => void;
}

export const CorrectModal = forwardRef(
  ({ onCheck }: CorrectModalProps, ref: Ref<CorrectModalRef>) => {
    const { t } = useTranslation();

    const { messageApi } = useMessage();

    const [showing, setShowing] = useState(false);

    const [transfer, setTransfer] = useState<TransferRead>();

    const [form] = Form.useForm<TransferCorrect>();

    const handleConfirm = async () => {
      if (!transfer) return;

      const formValue = await form.validateFields();

      Modal.confirm({
        okType: "danger",
        title: t("systems.Modal.confirmToCorrect"),
        icon: <ExclamationCircleFilled />,
        content: (
          <div>
            <Trans
              i18nKey="systems.Modal.warningCorrectResultUnitCannotRestore"
              values={{ unit: formValue.result }}
              components={{
                0: <span className="mx:4 f:18 f:bold fg:error" />,
              }}
            />
          </div>
        ),
        async onOk() {
          try {
            await TransferService.renew(transfer.id, {
              ...formValue,
              // 替代 textarea 的換行符號
              transaction: formValue.transaction
                ? formValue.transaction.replaceAll("\n", ",")
                : undefined,
            }).run();
            setShowing(false);
            onCheck();
          } catch (e) {
            if (ErrorHelper.isAxiosError(e)) {
              void ErrorHelper.handleAxiosErrorMessage(e, messageApi);
            } else {
              console.log(e);
            }
            return Promise.reject();
          }
        },
      });
    };

    const handleCancel = () => {
      setShowing(false);
    };

    useImperativeHandle(
      ref,
      () => {
        const open = (_transfer: TransferRead) => {
          form.setFieldsValue({
            result: _transfer.result,
            note: _transfer.note,
            // 替代 textarea 的換行符號
            transaction: _transfer.transaction
              ? _transfer.transaction.replaceAll(",", "\n")
              : "",
          });

          setTransfer(_transfer);

          setShowing(true);
        };

        return { open };
      },
      [form]
    );

    return (
      <Modal
        title={t("systems.Modal.memberDepositCorrect")}
        open={showing}
        footer={[
          <div className="flex jc:end gap:8" key="footer">
            <Button key="back" onClick={handleCancel}>
              {t("systems.Action.cancel")}
            </Button>
            <Button key="submit" type="primary" onClick={handleConfirm}>
              {t("systems.Action.submit")}
            </Button>
          </div>,
        ]}
        onCancel={handleCancel}
      >
        <Form form={form} labelCol={{ span: 4 }}>
          <Form.Item label={t("domains.Transfer.id")}>
            <Input readOnly value={transfer?.id} />
          </Form.Item>

          <Form.Item label={t("domains.Transfer.orderId")}>
            <Input readOnly value={transfer?.order?.id} />
          </Form.Item>

          <Form.Item label={t("domains.Transfer.memberId")}>
            <Input readOnly value={transfer?.member?.id} />
          </Form.Item>

          <Form.Item label={t("domains.Transfer.memberUsername")}>
            <Input readOnly value={transfer?.member?.username} />
          </Form.Item>

          <Form.Item label={t("domains.Transfer.memberName")}>
            <Input readOnly value={transfer?.member?.name} />
          </Form.Item>

          <Form.Item label={t("domains.Transfer.depositUnit")}>
            <Input readOnly type="number" value={transfer?.amount} />
          </Form.Item>

          <Form.Item
            label={t("domains.Transfer.resultUnit")}
            name="result"
            rules={[
              {
                required: true,
                message: t("systems.Error.cannotBeEmpty", {
                  field: t("domains.Transfer.resultUnit"),
                }),
              },
            ]}
          >
            <Input
              type="number"
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Transfer.resultUnit"),
              })}
            />
          </Form.Item>

          <Form.Item label={t("domains.Transfer.depositNote")} name="note">
            <Input
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Transfer.depositNote"),
              })}
            />
          </Form.Item>

          <Form.Item
            label={t("domains.Transfer.transactionHash")}
            name="transaction"
            extra={t("systems.Extra.inputEachOnASeparateLineIfMultiple", {
              field: t("domains.Transfer.transaction"),
            })}
          >
            <Input.TextArea
              placeholder={t("systems.Placeholder.pleaseInput", {
                field: t("domains.Transfer.transactionHash"),
              })}
              rows={4}
            />
          </Form.Item>
        </Form>
      </Modal>
    );
  }
);
